import type { BeforeSendEvent } from "@vercel/analytics";

const ANALYTICS_PREFERENCE_KEY = "control-exchange:analytics-preference:v1";
export const ANALYTICS_PREFERENCE_EVENT = "control-exchange:analytics-preference-changed";
let runtimePreference: boolean | null = null;

const PRIVATE_ROUTE_PATTERNS = [
  /^\/(?:auth|reset-password|forgot-password|projects)(?:\/|$)/,
  /^\/admin(?:\/|$)/,
  /^\/account(?:\/|$)/,
  /^\/sourcing-help(?:\/|$)/,
  /^\/dashboard(?:\/|$)/,
  /^\/messages(?:\/|$)/,
  /^\/notifications(?:\/|$)/,
  /^\/checkout(?:\/|$)/,
  /^\/cart(?:\/|$)/,
  /^\/order(?:\/|$)/,
  /^\/relay(?:\/|$)/,
  /^\/sell(?:\/|$)/,
  /^\/listing\/edit(?:\/|$)/,
  /^\/seller\/(?:sourcing|onboarding|connect-onboarding)(?:\/|$)/,
  /^\/shipping(?:\/|$)/,
  /^\/sourcing\/respond(?:\/|$)/,
];

const PUBLIC_ROUTE_TEMPLATES: Array<[RegExp, string]> = [
  [/^\/requests\/new\/?$/, "/requests/new"],
  [/^\/buy\/[^/]+\/[^/]+\/?$/, "/buy/[slug]/[id]"],
  [/^\/parts\/[^/]+\/?$/, "/parts/[slug]"],
  [/^\/requests\/[^/]+\/?$/, "/requests/[id]"],
  [/^\/research\/[^/]+\/?$/, "/research/[id]"],
  [/^\/category\/[^/]+\/?$/, "/category/[slug]"],
  [/^\/brand\/[^/]+\/?$/, "/brand/[slug]"],
  [/^\/discussions\/[^/]+\/?$/, "/discussions/[id]"],
  [/^\/p\/[^/]+\/?$/, "/p/[id]"],
  [/^\/listing\/[^/]+\/?$/, "/listing/[id]"],
  [/^\/@[^/]+\/?$/, "/@[handle]"],
];

const STATIC_TOP_LEVEL_ROUTES = new Set([
  "",
  "browse",
  "search",
  "price-check",
  "requests",
  "research",
  "request-a-part",
  "signup",
  "login",
  "forgot-password",
  "seller",
  "contact",
  "terms",
  "privacy",
  "returns",
  "refunds",
  "carriers",
  "discussions",
  "become-a-seller",
  "resellers",
  "pricing",
  "mission-statement",
]);

function browserSignalsOptOut(): boolean {
  if (typeof navigator === "undefined") return false;
  const navigatorWithPrivacy = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  return navigatorWithPrivacy.globalPrivacyControl === true
    || navigator.doNotTrack === "1"
    || navigatorWithPrivacy.msDoNotTrack === "1";
}

export function analyticsAllowed(): boolean {
  if (browserSignalsOptOut()) return false;
  if (runtimePreference !== null) return runtimePreference;
  try {
    return localStorage.getItem(ANALYTICS_PREFERENCE_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setAnalyticsAllowed(allowed: boolean): void {
  let persisted = false;
  try {
    localStorage.setItem(ANALYTICS_PREFERENCE_KEY, allowed ? "on" : "off");
    persisted = true;
  } catch {
    // Fall back to an in-memory preference for this page.
  }
  runtimePreference = persisted ? null : allowed;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(ANALYTICS_PREFERENCE_EVENT, { detail: { allowed: analyticsAllowed() } }));
  }
}

export function privacySafeAnalyticsPath(pathname: string): string | null {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (PRIVATE_ROUTE_PATTERNS.some((pattern) => pattern.test(normalized))) return null;

  for (const [pattern, template] of PUBLIC_ROUTE_TEMPLATES) {
    if (pattern.test(normalized)) return template;
  }

  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 1 && !STATIC_TOP_LEVEL_ROUTES.has(segments[0])) return "/store/[handle]";

  // Unknown nested routes may contain account or record identifiers. Suppress
  // them until an explicitly privacy-reviewed route template is added above.
  return segments.length <= 1 ? normalized : null;
}

export function privacySafeAnalyticsEvent(event: BeforeSendEvent): BeforeSendEvent | null {
  if (!analyticsAllowed()) return null;
  try {
    const url = new URL(event.url, typeof window === "undefined" ? "https://analytics.invalid" : window.location.origin);
    const safePath = privacySafeAnalyticsPath(url.pathname);
    if (!safePath) return null;
    url.pathname = safePath;
    url.search = "";
    url.hash = "";
    return { ...event, url: url.toString() };
  } catch {
    return null;
  }
}
